import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { AutoTagPreview, parseAutoTagPreview } from '../lib/autoTags';
import type { ProductSummary } from './useProviderProducts';

const EMPTY: AutoTagPreview = { enabled: false, tags: {} };

/**
 * The automatic tags the merchant's store would give the products on screen, before any
 * sync. Same SQL as the sync, so it shows exactly what will be written. Merchant view only —
 * pass a null merchantId otherwise. A failed lookup just means no tags shown.
 */
export const useAutoTags = (merchantId: string | null, products: ProductSummary[]) => {
  const [preview, setPreview] = useState<AutoTagPreview>(EMPTY);

  useEffect(() => {
    if (!merchantId || products.length === 0) {
      setPreview(EMPTY);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc('preview_auto_tags', {
          p_merchant_id: merchantId,
          p_product_ids: products.map((p) => p.product_id),
        });
        if (!cancelled) setPreview(error ? EMPTY : parseAutoTagPreview(data));
      } catch {
        if (!cancelled) setPreview(EMPTY);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [merchantId, products]);

  return preview;
};
